import {
  getOfferConditionsUrl,
  useOpenClinicianCheckout,
} from '@/app-shell/complimentary-trial-end/clinician-upgrade-checkout-route';
import {
  useIsHeidi2ClinicianTrialEndEnabled,
  useIsHeidi2ClinicianTrialOfferEnabled,
} from '@/app-shell/flags/growth-flags';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackClinicianTrialEndedModalClick,
  trackClinicianTrialEndedModalViewed,
} from '@/models/analytics/features/clinician-trial-end-analytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  ClinicianTrialEndedUsageClass,
  Heidi2ClinicianTrialEndedModal,
  useClinicianTrialYearlyMaxOffer,
  type ClinicianTrialEndedUsage,
} from '@heidi/commerce';
import type {
  ClinicianTrialEndedAnalyticsContext,
  ClinicianTrialEndedModalAction,
} from '@heidi/commerce/contracts';
import {
  CLINICIAN_TRIAL_ENDED_MODAL_ID,
  getComparePlansUrl,
  getUsageLimitsHelpUrl,
} from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { ExternalLink, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

const OpenHeidi2ClinicianTrialEndedModal = ({
  usage,
}: {
  usage: ClinicianTrialEndedUsage;
}) => {
  const intl = useIntl();
  const { closeWithOptions } = useQueuedModal(CLINICIAN_TRIAL_ENDED_MODAL_ID);
  const openClinicianCheckout = useOpenClinicianCheckout();
  const offer = useClinicianTrialYearlyMaxOffer({
    isTrialEndEnabled: useIsHeidi2ClinicianTrialEndEnabled(),
    isAppleCompliantBuild: isAppleCompliantBuild(),
    isOfferEnabled: useIsHeidi2ClinicianTrialOfferEnabled(),
  });
  const isMoreThanStandard =
    usage.usageClass === ClinicianTrialEndedUsageClass.MoreThanStandard;
  const analyticsContext = useMemo<ClinicianTrialEndedAnalyticsContext>(
    () => ({
      targetSeat: isMoreThanStandard ? 'max' : 'standard',
      usagePercent: usage.usagePercent,
    }),
    [isMoreThanStandard, usage.usagePercent]
  );

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackClinicianTrialEndedModalViewed(analyticsContext);
  }, [analyticsContext]);

  const trackClick = useCallback(
    (action: ClinicianTrialEndedModalAction, label: string) =>
      trackClinicianTrialEndedModalClick({ ...analyticsContext, action, label }),
    [analyticsContext]
  );

  const handleUpgrade = useCallback(() => {
    trackClick('upgrade', 'Keep using Clinician');
    closeWithOptions({ action: ModalViewAction.engage });
    openClinicianCheckout({ seat: analyticsContext.targetSeat });
  }, [analyticsContext.targetSeat, closeWithOptions, openClinicianCheckout, trackClick]);

  const handleUseFree = useCallback(() => {
    trackClick('use_free', 'Downgrade to Heidi Free');
    closeWithOptions({ action: ModalViewAction.dismiss });
    toast({
      tone: 'subtle',
      icon: <Info aria-hidden />,
      title: intl.formatMessage({
        defaultMessage: 'You’re now on Heidi Free',
        id: 'sNC8U1/DNU',
        description:
          'Toast after a member chooses Heidi Free from the Clinician trial ended modal',
      }),
      action: (
        <Button asChild variant="outline" size="xs">
          <a
            href={getComparePlansUrl(intl.locale)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage({
              defaultMessage: 'See inclusions',
              id: 'RwCh39cI4t',
              description:
                'Toast action after choosing Heidi Free; opens the plan comparison on the pricing page',
            })}
            <ExternalLink aria-hidden />
          </a>
        </Button>
      ),
    });
  }, [closeWithOptions, intl, trackClick]);

  // The dialog only reports user dismissals (×, overlay, Escape, drag).
  const handleDismiss = useCallback(() => {
    trackClick('close', 'close');
    closeWithOptions({ action: ModalViewAction.dismiss });
  }, [closeWithOptions, trackClick]);

  return (
    <Heidi2ClinicianTrialEndedModal
      open
      usageClass={usage.usageClass}
      usageLimitsHref={getUsageLimitsHelpUrl(intl.locale)}
      offer={
        offer == null
          ? null
          : {
              cycleEnd: offer.cycleEnd,
              conditionsHref: getOfferConditionsUrl(intl.locale),
            }
      }
      onUpgrade={handleUpgrade}
      onUseFree={handleUseFree}
      onDismiss={handleDismiss}
    />
  );
};

/** Queue mount for the one-time modal once a new user's Clinician trial has ended. */
export const Heidi2ClinicianTrialEndedModalHost = ({
  usage,
}: {
  usage: ClinicianTrialEndedUsage | null;
}) => {
  const { isOpen } = useQueuedModal(CLINICIAN_TRIAL_ENDED_MODAL_ID);

  if (!isOpen || usage == null) return null;

  return <OpenHeidi2ClinicianTrialEndedModal usage={usage} />;
};
